import React, { useState, useEffect, useRef } from 'react';

/**
 * LazyImage component with loading placeholder and error handling
 * Implements Intersection Observer API for efficient lazy loading
 */
const LazyImage = ({ 
  src, 
  alt = '', 
  className = '', 
  placeholderClassName = '',
  errorImage = 'https://atozpropertysolutionsltd.com/property-not-found.svg',
  loading = 'lazy',
  style = {},
  ...props 
}) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const imgRef = useRef(null);
  const observerRef = useRef(null);

  useEffect(() => {
    // Reset state when src changes
    setIsLoaded(false);
    setHasError(false);
    setIsInView(false);

    // Validate src
    if (!src || typeof src !== 'string' || src.trim() === '') {
      setHasError(true);
      setImageSrc(errorImage);
      return;
    }
    
    const trimmedSrc = src.trim();
    
    // Handle placeholder URLs directly
    if (trimmedSrc === '/placeholder.svg' || 
        trimmedSrc === '/api/placeholder/300/200' || 
        trimmedSrc.startsWith('/api/placeholder')) {
      setImageSrc(trimmedSrc);
      setIsInView(true);
      return;
    }

    // Normalize the image URL
    let normalizedSrc = trimmedSrc;
    if (!normalizedSrc.startsWith('http://') && 
        !normalizedSrc.startsWith('https://') && 
        !normalizedSrc.startsWith('/') && 
        !normalizedSrc.startsWith('data:')) {
      normalizedSrc = '/' + normalizedSrc;
    }

    // Create Intersection Observer
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setImageSrc(normalizedSrc);
            setIsInView(true);
            if (observerRef.current && entry.target) {
              observerRef.current.unobserve(entry.target);
            }
          }
        });
      },
      {
        rootMargin: '100px', // Start loading 100px before entering viewport
        threshold: 0.01
      }
    );

    if (imgRef.current) {
      observerRef.current.observe(imgRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [src, errorImage]);

  const handleLoad = () => {
    setIsLoaded(true);
    setHasError(false);
  };

  const handleError = () => {
    setHasError(true);
    setIsLoaded(true);
    setImageSrc(errorImage);
  };

  const showPlaceholder = !isLoaded && !hasError && isInView;

  return (
    <div 
      ref={imgRef}
      className={`relative overflow-hidden ${className}`}
      style={style}
    >
      {/* Loading placeholder - only show while image is actually loading */}
      {showPlaceholder && (
        <div 
          className={`absolute inset-0 bg-gradient-to-br from-gray-200 to-gray-300 animate-pulse ${placeholderClassName}`}
        />
      )}
      
      {/* Actual image - render immediately when src is set */}
      {imageSrc && (
        <img
          src={imageSrc}
          alt={alt}
          className={`w-full h-full object-cover transition-opacity duration-300 ${
            isLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          onLoad={handleLoad}
          onError={handleError}
          loading={loading}
          {...props}
        />
      )}
      
      {/* Fallback for no image source yet */}
      {!imageSrc && !isInView && (
        <div className={`absolute inset-0 bg-gray-100 ${placeholderClassName}`} />
      )}
    </div>
  );
};

export default LazyImage;